import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    brands = "brands",
}

export interface ServiceSectionBrandModel {
    fields: {
        displayName: string;
        brandImage: string;
        title: string;
        description: string;
        linkTo: LinkFieldValue;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.brands]: ServiceSectionBrandModel[];
    };
}

export interface ServiceSectionBrandsStyles {
    container?: InjectableCss;
    brand?: InjectableCss;
    card?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    image: InjectableCss;
}

export const serviceSectionBrandsStyles: ServiceSectionBrandsStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            row-gap: 45px;
            column-gap: 50px;
        `,
    },
    image: {
        css: css`
            height: 120px;
        `,
    },
    title: {
        variant: "h4",
        color: "common.accent",
        css: css`
            margin-bottom: 5px;
        `,
    },
    description: {
        variant: "p",
        css: css`
            margin-bottom: 0;
            > p {
                margin-bottom: 0;
            }
        `,
    },
    brand: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            flex-basis: calc(50% - 75px);
                            max-width: calc(50% - 75px);
                        `,
                        css`
                            flex-basis: calc(33.3% - 50px);
                            max-width: calc(33.3% - 50px);
                        `,
                        css`
                            flex-basis: calc(33.3% - 50px);
                            max-width: calc(33.3% - 50px);
                        `,
                        css`
                            flex-basis: calc(25% - 37.5px);
                            max-width: calc(25% - 37.5px);
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = serviceSectionBrandsStyles;

export const ServiceSectionBrands: FC<OwnProps> = ({ fields }) => {
    const links = useGetLinks(fields.brands, o => o.fields.linkTo);
    return (
        <StyledWrapper {...styles.container}>
            {fields.brands.map((brand, i) => {
                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper {...styles.brand} key={i}>
                        {brand.fields.brandImage && (
                            <Link href={links[i].url} target={brand.fields.openInNewWindow ? "_blank" : ""}>
                                <LazyImage {...styles.image} src={brand.fields.brandImage}></LazyImage>
                            </Link>
                        )}
                        {brand.fields.title && (
                            <Link href={links[i].url} target={brand.fields.openInNewWindow ? "_blank" : ""}>
                                <Typography {...styles.title}>{brand.fields.title}</Typography>
                            </Link>
                        )}

                        <Typography {...styles.description}>
                            {parse(brand.fields.description, parserOptions)}
                        </Typography>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: ServiceSectionBrands,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.brands,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.displayName;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "displayName",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "brandImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTo",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
